// src/features/messages/components/MessageListItem.jsx
import React from 'react';
import { Badge, Box, ListItemButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import { ChatBubbleOutline as CommentIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ProfilePicture from '../../../components/common/ProfilePicture';
import AudienceLine from './AudienceLine';
import MessageTime from './MessageTime';
import { REACTION_EMOJI } from '../constants';

// Strong enough to spot in dark mode too, where action.hover all but vanishes
const unreadBg = (theme, extra = 0) => alpha(theme.palette.primary.main, (theme.palette.mode === 'dark' ? 0.2 : 0.07) + extra);

/**
 * A message in the inbox: who, when it was sent (as in the opened message),
 * the start of the text and its activity.
 */
export default function MessageListItem({ message, onClick }) {
  const { t } = useTranslation(['messages']);
  const unread = !message.is_read;
  const newComments = message.unread_comment_count;
  const highlight = unread || (message.is_following && newComments > 0);

  return (
    <ListItemButton
      onClick={() => onClick(message)}
      alignItems="flex-start"
      sx={{
        gap: 1.5,
        py: 1.25,
        borderBottom: 1,
        borderColor: 'divider',
        borderLeft: 3,
        borderLeftColor: unread ? 'primary.main' : 'transparent',
        ...(unread && {
          bgcolor: (theme) => unreadBg(theme),
          '&:hover': { bgcolor: (theme) => unreadBg(theme, 0.05) },
        }),
      }}
    >
      <Badge color="primary" variant="dot" invisible={!highlight} overlap="circular">
        <ProfilePicture
          profilePictureFileId={message.author?.profile_picture_file_id}
          name={message.author?.name}
          color={message.author?.color}
          size={36}
          bgcolor="grey.400"
        />
      </Badge>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="body2" fontWeight={unread ? 700 : 500} noWrap sx={{ flex: 1 }}>
            {unread && <Box component="span" sx={visuallyHidden}>{t('messages:list.unread')}: </Box>}
            {message.is_author ? t('messages:you') : (message.author?.name ?? t('messages:deleted_account'))}
          </Typography>
          <MessageTime date={message.created_at} />
        </Box>
        {/* Nearly everything goes to the whole department; only say so when it doesn't */}
        {!message.audience?.all && <AudienceLine audience={message.audience} compact />}
        <Typography
          variant="body2"
          sx={{
            mt: 0.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            whiteSpace: 'pre-line',
            wordBreak: 'break-word',
          }}
        >
          {message.content}
        </Typography>
        {(message.comment_count > 0 || message.reactions.length > 0) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
            {message.reactions.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {message.reactions.map(r => `${REACTION_EMOJI[r.reaction] ?? ''}${r.count}`).join('  ')}
              </Typography>
            )}
            {message.comment_count > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CommentIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {message.comment_count}
                </Typography>
                {newComments > 0 && (
                  <Typography variant="caption" color="primary" fontWeight={600}>
                    {t('messages:list.new_comments', { count: newComments })}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </ListItemButton>
  );
}
